import type { TicketEvent } from './Ticket.types';

/**
 * Process ticket events to apply deletions and edits.
 *
 * This function handles:
 * - DELETE_MESSAGE, DELETE_ATTACHMENT, DELETE_LOCATION: Removes the target event
 * - UPDATE_MESSAGE, UPDATE_LOCATION: Merges updates into the target event's payload
 *
 * The result is a cleaned list of events where deleted events are removed
 * and edited events reflect their latest state.
 *
 * @param events - Array of ticket events in chronological order
 * @returns Processed events with deletions applied and edits merged
 */
export function processTimelineEvents<T extends TicketEvent>(events: T[]): T[] {
  const eventMap = new Map<string, T>();

  for (const event of events) {
    switch (event.type) {
      case 'DELETE_MESSAGE':
      case 'DELETE_ATTACHMENT':
      case 'DELETE_LOCATION':
        // Remove the target event
        eventMap.delete(event.payload.eventId);
        break;

      case 'UPDATE_MESSAGE':
      case 'UPDATE_LOCATION': {
        // Merge update into the target event's payload
        const { eventId, ...rest } = event.payload;
        const targetEvent = eventMap.get(eventId);
        if (targetEvent) {
          targetEvent.payload = { ...targetEvent.payload, ...rest };
        }
        break;
      }

      default:
        // Add all other events to the map
        eventMap.set(event.id, event);
        break;
    }
  }

  return Array.from(eventMap.values());
}
